import s from '@/pages/ConnectorInfo/ConnectorInfo.module.scss';
import { Flex } from '@tremor/react';
import { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConnectorAuth } from '@/hooks/useConnectorAuth';
import { isGoogleAnalyticsConnectionType } from '@utils/typeGuards';
import { assert } from '@/utils/asserts';
import { ConnectorTitle } from '../ConnectorTitle/ConnectorTitle';
import { ConnectionSteps } from '@/components/ConnectionSteps/ConnectionSteps';
import { GOOGLE_ANALYTICS_INFO_STEPS, NAV_ROUTES } from '@/utils/constants';
import { ConnectorSubtitle } from '../ConnectorSubtitle/ConnectorSubtitle';
import { ConnectorRadioButton } from '../../Inputs/ConnectorRadioButton/ConnectorRadioButton';
import { BottomActions } from '../BottomActions/BottomActions';

const defaultValChecked = 'oauth';

const scope = `https://www.googleapis.com/auth/analytics.edit https://www.googleapis.com/auth/analytics https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email`;

const parsedScope = encodeURIComponent(scope);

export const GoogleAnalyticsInfoConnector = () => {
  const navigate = useNavigate();
  const [setSelectedMethod, , isLoading, generateOAuthLink] = useConnectorAuth(
    defaultValChecked,
    {
      platform: 'google-analytics',
      OAuthUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
      clientID: import.meta.env._VITE_GOOGLE_APP_ID,
    },
    'prompt=consent&response_type=code&access_type=offline',
  );

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const selectedConnectionType = e.currentTarget.elements.namedItem('connector');
    assert(selectedConnectionType !== null, 'No connection type selected');
    assert('value' in selectedConnectionType, 'No value in selected connection type');
    const selectedConnectionTypeValue = selectedConnectionType.value;
    isGoogleAnalyticsConnectionType(selectedConnectionTypeValue);
    switch (selectedConnectionTypeValue) {
      case 'oauth':
        window.location.href = await generateOAuthLink(parsedScope);
        break;
      case 'manual':
        navigate(NAV_ROUTES.connectorEdit('google-analytics'));
        break;
    }
  };

  return (
    <form className={s.form} onSubmit={handleSubmit}>
      <ConnectorTitle connectorId="google-analytics">Google Analytics</ConnectorTitle>
      <ConnectionSteps
        style={{ marginBottom: '40px' }}
        steps={GOOGLE_ANALYTICS_INFO_STEPS}
        currentStep={1}
      />
      <ConnectorSubtitle>
        Choose what data you&apos;d like to share with us:
      </ConnectorSubtitle>
      <Flex
        flexDirection="col"
        style={{ gap: '20px' }}
        onChange={(e) => setSelectedMethod((e.target as HTMLInputElement).value)}
      >
        <ConnectorRadioButton
          value="oauth"
          name="connector"
          label="Aggregated reports"
          defaultChecked
          description="Connect your historical performance data to fuel your reports"
        />
        <BottomActions isCreating={true} isLoading={isLoading} />
      </Flex>
    </form>
  );
};
